import { z } from "zod";
import { CompanyUpdate } from "./company.js";
import { Estimate, EstimateStatus, LineInput, RevisionDiffRow, SheetInput, type LaborBreakdown } from "./estimate.js";
import { daysBetween } from "./payment.js";
import { PaperPresetKey, PaperRef, PaperSource } from "./paper.js";
import { PaperLayout } from "./paperLayout.js";
import { OrderOrigins } from "./orderBundle.js";
import type { Product } from "./product.js";
import { UNISSUED_MSG } from "./unissued.js";

/**
 * 注文書（発注書）と注文請書の電子承諾の1回目（#233、docs/93。2026-10-24 射場さんの決定 ★1〜★7・§12 の 12 点）。ここ1か所が正本：
 *   - 帳票の名前は会社の言葉 {order}・{orderAck}（現場力＝注文書／注文請書・営業力＝発注書／発注請書）。画面・メール・紙に「注文書」「発注書」を文字で書かない
 *   - 条件（受領期日・納品場所・検査完了期日・支払期日・支払方法・約款・変更協議）の形と既定、発行前の確認（要修正／注意／参考）の決まり
 *   - 電子承諾の3段（会社 → {vendor}ごと → その1回）の解き方、{vendor}の種別（工事／仕入）の既定
 *   - 承諾ページ（/o/<鍵>）・{vendor}の詳細の節・設定の画面の形と文
 * 金額は DB が計算する（見積と同じ fn_recalc_all。消費税は税率ごとに1回＝ルール1）。この紙の金額は仕入れそのもの（社内用の列は無い）
 */

/* ============================================================================
 * 決まり
 * ========================================================================== */

/** {vendor}の種別（partners.order_work_kind）：construction＝工事（建設工事の請負＝約款添付型の紙）／supply＝仕入（売買＝取適法の明示） */
export const OrderWorkKind = z.enum(["construction", "supply"]);
export type OrderWorkKind = z.infer<typeof OrderWorkKind>;
export const ORDER_WORK_KIND_LABEL: Record<OrderWorkKind, string> = { construction: "工事", supply: "仕入" };
/** 種別の既定（docs/93 §12 の 10）：現場力＝工事・営業力＝仕入 */
export const orderWorkKindDefault = (product: Product): OrderWorkKind => (product === "genba" ? "construction" : "supply");

/**
 * 承諾の方法（#253、docs/93 §16・§17）：none＝{orderAck}を出さない（発行で完了）／electronic＝電子承諾（承諾ページ）／paper＝書面（書面の{orderAck}を返送）。
 * 画面は「{orderAck}」のスイッチ（オフ＝none）と受け取り方（電子承諾｜書面）＝#256。データはこの3つのまま。
 * 会社の既定（order_settings.ackMode・無ければ none）→ {vendor}ごと（partners.order_ack_mode・null＝会社に従う）→ その1回（下書きの order_terms.ack＝条件の帯。#257）。
 * 出す瞬間に回に固める（template_snapshot.order.ack）。前に出した回（無い）は承諾ページの鍵があれば電子・無ければ書面
 */
export const OrderAckMode = z.enum(["none", "electronic", "paper"]);
export type OrderAckMode = z.infer<typeof OrderAckMode>;
/** 承諾の方法の名前（要約・発行前の確認の1行。#262＝「紙」でなく「書面」） */
export const ORDER_ACK_LABEL: Record<OrderAckMode, string> = { none: "なし", electronic: "電子承諾", paper: "書面" };
/** 受け取り方（スイッチがオンのとき。#256）：電子承諾｜書面 */
export const ORDER_ACK_RECEIVE = ["electronic", "paper"] as const satisfies ReadonlyArray<OrderAckMode>;
/** スイッチをオンにした瞬間の受け取り方（#256・§17.3 ロ）：会社は書面、{vendor}・{order}は上の段の受け取り方（オフなら書面） */
export const orderAckTurnOn = (above: OrderAckMode | null | undefined): OrderAckMode => (above && above !== "none" ? above : "paper");

/** 支払方法（取適法：手形払いは全面禁止＝選択肢に無い） */
export const OrderPayMethod = z.enum(["transfer", "densai", "factoring"]);
export type OrderPayMethod = z.infer<typeof OrderPayMethod>;
export const ORDER_PAY_METHOD_LABEL: Record<OrderPayMethod, string> = { transfer: "振込", densai: "電子記録債権", factoring: "一括決済" };

/** 約款（§5.1・§14.2）：standard＝国土交通省の建設工事標準下請契約約款／own＝自社の PDF／none＝付けない（会社の設定「添付しない」） */
export const OrderTermsKind = z.enum(["standard", "own", "none"]);
export type OrderTermsKind = z.infer<typeof OrderTermsKind>;
/** 同梱の約款（apps/api/assets/order-terms/ の PDF）。版は出す瞬間に template_snapshot に固める */
export const STANDARD_TERMS = { name: "建設工事標準下請契約約款（国土交通省）", short: "標準約款（国交省）", version: "mlit-20251202" } as const;

/** 変更協議の既定の文（docs/93 §5.1：「協議して定める。協議に当たっては価格等の変動の内容…を考慮する」型） */
export const ORDER_NEGOTIATION_DEFAULT =
  "工期内に賃金・物価の変動があったときは、請負代金額の変更を求めることができる。協議にあたっては、変動の内容と労務費の水準を考慮する。";
/** 「変更しない」型の文（建設は止める＝§5.2） */
const NO_CHANGE_RE = /(変更(は|を)?(し|でき|認め|行わ)ない|一切(の)?変更|変更(に|には)応じない|変更不可)/;
export const isNoChangeNegotiation = (text: string): boolean => NO_CHANGE_RE.test(text.replace(/\s/g, ""));

/** 上限（DB の CHECK と同じ） */
export const ORDER_LIMITS = {
  /** 承諾ページの期限（日。§12 の 7） */
  linkDays: 60,
  negotiation: 1000,
  tbdReason: 200,
  /** 取適法：支払期日は受領から 60日以内 */
  payDays: 60,
  /** 帳票の名前（言葉の order・orderAck） */
  name: 12,
  /** 収入印紙の参考を出す税抜の金額（請負の契約書は 1万円以上が課税。#252） */
  stampFrom: 10_000,
} as const;

/**
 * 未定の欄（#251、docs/93 §16）：「未定」のチェックだけで発行できる＝理由と予定日は任意（社内の控え。紙には印字しない）。
 * 未定の欄は値（受領・検査完了・支払の日付と納品場所）を空にする（clearOrderTbd・API の保存と発行）
 */
export const OrderTbd = z.object({ reason: z.string().trim().max(200).default(""), on: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().default(null) });
export type OrderTbd = z.infer<typeof OrderTbd>;
export const ORDER_TBD_KEYS = ["receive", "place", "inspect", "pay"] as const;
export type OrderTbdKey = (typeof ORDER_TBD_KEYS)[number];

const Day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().default(null);
/**
 * 基本契約書（#233 2c、docs/93 §14.2・★9）：{vendor}と自社で交わした契約書の記録（締結日・名称）。
 * 記録のある工事の{vendor}への{order}は約款を付けず「本{order}に記載のない事項は ○年○月○日付 ○○による」の1行（契約書そのものはこのシステムでは交わさない）
 */
export const BasicContractTerm = z.object({ on: z.string().regex(/^\d{4}-\d{2}-\d{2}$/), name: z.string().trim().min(1).max(60) });
export type BasicContractTerm = z.infer<typeof BasicContractTerm>;
/** 回の条件（document_revisions.order_terms。下書きで直し、出すと固まる）。納品場所の住所は回の siteAddress（暗号化） */
export const OrderTerms = z.object({
  v: z.literal(1).default(1),
  /** 受領期日（現場力は工期＝着手〜完成。from は空でもよい） */
  receiveFrom: Day,
  receiveTo: Day,
  inspectOn: Day,
  payOn: Day,
  method: OrderPayMethod.default("transfer"),
  terms: OrderTermsKind.default("none"),
  /** 約款の版・名前（出す瞬間に API が入れる。下書きは今の設定） */
  termsVersion: z.string().max(80).default(""),
  termsName: z.string().max(60).default(""),
  negotiation: z.string().max(1000).default(""),
  /**
   * 変更協議を付けるか（#255、docs/93 §16）。下書き＝その1回（初めは会社の設定）・出す瞬間に固める。
   * 無い回（前に出した回）は今までどおり付ける＝orderNegotiationOn が true
   */
  negotiationOn: z.boolean().optional(),
  /**
   * その1回の承諾の選び（#257、docs/93 §17）：条件の帯の「{orderAck}」のスイッチと受け取り方。無い下書きは {vendor} → 会社の既定。
   * 発行の瞬間は template_snapshot.order.ack に固める（ここも一緒に残る）
   */
  ack: OrderAckMode.optional(),
  tbd: z.partialRecord(z.enum(ORDER_TBD_KEYS), OrderTbd).default({}),
  /** 基本契約書（出す瞬間に API が{vendor}の記録から入れる。下書きは null＝今の記録は Order.basicContract） */
  basicContract: BasicContractTerm.nullable().default(null),
});
export type OrderTerms = z.infer<typeof OrderTerms>;
/** 変更協議を付ける回か（無い＝前に出した回は付ける） */
export const orderNegotiationOn = (t: Pick<OrderTerms, "negotiationOn">): boolean => t.negotiationOn !== false;
/**
 * 下書きで変更協議を付けるか（#261、docs/93 §17.3 ト）：スイッチがオン かつ 約款を付ける（基本契約書の記録があれば約款の代わり）。
 * 約款の付かない{order}は帯に変更協議の行が無い＝発行前の確認にも出さず、発行の瞬間に negotiationOn false で固める（前に出した回の紙は orderNegotiationOn のまま）
 */
export const orderNegotiationApplies = (t: Pick<OrderTerms, "negotiationOn" | "terms">, basicContract: boolean): boolean => orderNegotiationOn(t) && (t.terms !== "none" || basicContract);
/** 未定の欄の値を空にする（#251。未定が勝つ＝画面と API の保存・発行で同じ）。納品場所は回の siteAddress なので place で返す */
export function clearOrderTbd<T extends Pick<OrderTerms, "tbd" | "receiveFrom" | "receiveTo" | "inspectOn" | "payOn">>(t: T, place: string): { terms: T; place: string } {
  return {
    terms: {
      ...t,
      ...(t.tbd.receive ? { receiveFrom: null, receiveTo: null } : {}),
      ...(t.tbd.inspect ? { inspectOn: null } : {}),
      ...(t.tbd.pay ? { payOn: null } : {}),
    },
    place: t.tbd.place ? "" : place,
  };
}

/** 会社の設定（tenants.order_settings）。無いキーは既定 */
export const OrderSettingsRaw = z.object({
  /** 承諾の既定（#253。無ければ none） */
  ackMode: OrderAckMode.optional(),
  termsKind: OrderTermsKind.optional(),
  termsAssetId: z.string().uuid().optional(),
  termsName: z.string().max(60).optional(),
  negotiation: z.string().trim().min(1).max(1000).optional(),
  /** 変更協議を付ける既定（#255。無ければ false＝両製品） */
  negotiationOn: z.boolean().optional(),
  printBreakdown: z.boolean().optional(),
  printSeal: z.boolean().optional(),
  /** 印刷・FAX で約款を別紙で同封する既定（#233 2c。無ければオフ＝10/25 射場さん） */
  printTerms: z.boolean().optional(),
  /** {paymentNotice}の確認の期限（送付後 n日。3〜30・無ければ 7＝#233 3b） */
  confirmDays: z.number().int().min(3).max(30).optional(),
});
export type OrderSettingsRaw = z.infer<typeof OrderSettingsRaw>;
/** 解いた設定（既定を入れたもの） */
export const OrderSettings = z.object({
  /** 承諾の既定（なし／電子／紙。#253） */
  ackMode: OrderAckMode,
  /** 会社の既定の約款（standard＝標準／own＝自社の PDF／none＝添付しない） */
  termsKind: OrderTermsKind,
  /** 自社の約款の PDF（登録してあれば。既定が標準・添付しないでも残す＝「自社の PDF」に戻せる） */
  termsAssetId: z.string().uuid().nullable(),
  /** 既定の約款の名前（標準＝国交省の約款・自社＝PDF の名前・添付しない＝空） */
  termsName: z.string(),
  /** 自社の約款の PDF の名前 */
  ownTermsName: z.string(),
  negotiation: z.string(),
  /** 変更協議を付ける既定（#255） */
  negotiationOn: z.boolean(),
  printBreakdown: z.boolean(),
  printSeal: z.boolean(),
  /** 印刷・FAX で約款を別紙で同封する既定（無ければオフ） */
  printTerms: z.boolean(),
  /** {paymentNotice}の確認の期限（送付後 n日。#233 3b） */
  confirmDays: z.number().int(),
});
export type OrderSettings = z.infer<typeof OrderSettings>;
/**
 * 会社の設定を解く。承諾の既定は両製品とも なし（#253、2026-10-27 射場さんの決定＝今ある会社にも）・約款はオフ（#261）・変更協議は オフ（#255。約款オフならいつもオフ）。
 * product は前の形（製品ごとの電子承諾の既定）の名残り＝今は既定を変えない
 */
export function resolveOrderSettings(raw: unknown, _product: Product): OrderSettings {
  const r = OrderSettingsRaw.safeParse(raw ?? {});
  const v = r.success ? r.data : {};
  // 約款の既定はオフ（#261、docs/93 §17.3 イ＝選んだことの無い会社も）。自社の約款は PDF があるときだけ（無ければ標準）
  const kind: OrderTermsKind = !v.termsKind || v.termsKind === "none" ? "none" : v.termsKind === "own" && !!v.termsAssetId ? "own" : "standard";
  return {
    ackMode: v.ackMode ?? "none",
    termsKind: kind,
    termsAssetId: v.termsAssetId ?? null,
    termsName: kind === "own" ? (v.termsName ?? "") : kind === "standard" ? STANDARD_TERMS.name : "",
    ownTermsName: v.termsAssetId ? (v.termsName ?? "") : "",
    negotiation: v.negotiation ?? ORDER_NEGOTIATION_DEFAULT,
    // 約款オフなら変更協議もオフ（#261）
    negotiationOn: kind !== "none" && (v.negotiationOn ?? false),
    printBreakdown: v.printBreakdown ?? true,
    printSeal: v.printSeal ?? true,
    printTerms: v.printTerms ?? false,
    confirmDays: v.confirmDays ?? 7,
  };
}

/**
 * 承諾の3段（★4・#253・#257）：会社の既定 → {vendor}ごと → その1回（下書きの order_terms.ack＝条件の帯）。選んでいなければ {vendor}の承諾方法（無ければ会社の既定）
 */
export const orderAckInitial = (company: OrderAckMode, partner: OrderAckMode | null, draft?: OrderAckMode | null): OrderAckMode => draft ?? partner ?? company;
/**
 * 収入印紙の参考（#252、docs/93 §16）：承諾が書面・請負（建設の紙か{vendor}の種別が工事）・税抜 1万円以上のときだけ
 * （印紙税法の第2号文書＝請負に関する契約書。1万円未満は非課税。電子の承諾は文書の作成でない）
 */
export function orderStampNeeded(x: { ack: OrderAckMode; construction: boolean; workKind: OrderWorkKind; subtotal: number }): boolean {
  return x.ack === "paper" && (x.construction || x.workKind === "construction") && x.subtotal >= ORDER_LIMITS.stampFrom;
}
/** 建設の紙（約款添付型）か：field の会社 かつ {vendor}が工事 */
export const isConstructionOrder = (field: boolean, kind: OrderWorkKind): boolean => field && kind === "construction";

/** 名称のセット（★1。設定 → {order}「名称」）。帳票を選ぶと請書に対の名前が入る（請書は手で直せる） */
export const ORDER_NAME_PAIRS: ReadonlyArray<{ order: string; ack: string | null }> = [
  { order: "注文書", ack: "注文請書" },
  { order: "発注書", ack: "発注請書" },
  { order: "工事注文書", ack: "工事注文請書" },
  { order: "外注発注書", ack: "外注発注請書" },
  { order: "注文書兼請書", ack: null },
  { order: "発注書兼請書", ack: null },
  { order: "申込書", ack: "申込承諾書" },
];
/** 帳票の名前 → 請書の名前（兼請書は同じ名前・知らない名前は「名前＋請書」） */
export function orderAckNameFor(order: string): string {
  const p = ORDER_NAME_PAIRS.find((x) => x.order === order);
  if (p) return p.ack ?? order;
  return order.endsWith("書") ? `${order.slice(0, -1)}請書` : `${order}請書`;
}

/** 支払期日の基準（受領 → 検査完了のどちらか後）と日数。基準か支払期日が無ければ null */
export function orderPayDays(t: Pick<OrderTerms, "receiveTo" | "inspectOn" | "payOn">): { base: string; baseKind: "receive" | "inspect"; days: number } | null {
  if (!t.payOn) return null;
  const base = t.receiveTo ?? null;
  if (!base) return null;
  return { base, baseKind: "receive", days: daysBetween(t.payOn, base) };
}

/* ============================================================================
 * 発行前の確認（§5.2）
 * ========================================================================== */

/** 3種類はチップの文字で分ける：fix＝要修正（止める）／warn＝注意（出せる）／info＝参考（出せる） */
export const OrderCheckLevel = z.enum(["fix", "warn", "info"]);
export type OrderCheckLevel = z.infer<typeof OrderCheckLevel>;
export const OrderCheckItem = z.object({
  level: OrderCheckLevel,
  code: z.string(),
  text: z.string(),
  /** 画面の動き（入力の欄へ・標準の約款を付ける） */
  action: z.enum(["receive", "place", "inspect", "pay", "terms", "negotiation"]).nullable().default(null),
});
export type OrderCheckItem = z.infer<typeof OrderCheckItem>;
export const OrderCheck = z.object({ items: z.array(OrderCheckItem), canIssue: z.boolean() });
export type OrderCheck = z.infer<typeof OrderCheck>;

const yen = (n: number) => n.toLocaleString("ja-JP");
const md = (ymd: string) => `${Number(ymd.slice(5, 7))}/${Number(ymd.slice(8, 10))}`;
export type OrderCheckInput = {
  terms: OrderTerms;
  place: string;
  construction: boolean;
  field: boolean;
  /** この1回の承諾の方法（印紙の参考＝書面のときだけ。#252） */
  ack: OrderAckMode;
  /** {vendor}の種別（印紙の参考の「請負」。#252） */
  workKind: OrderWorkKind;
  subtotal: number;
  /** 元の{vendorQuote}の税抜の合計（無ければ null） */
  sourceTotal: number | null;
  /** 会社の自社の約款（PDF）が登録済みか（約款を「自社の約款」にしたときの確かめ） */
  ownTermsReady: boolean;
  /** {vendor}に基本契約書の記録があるか（あれば約款を付けない＝約款の行を出さない。#233 2c） */
  basicContract?: boolean;
  /** 明細の行（労務費の内訳の数え） */
  lines: ReadonlyArray<Pick<LineInput, "role"> & { laborBreakdown?: LaborBreakdown | null | undefined }>;
};
/** 確かめ（API の POST /orders/:id/check と画面が同じ関数）。文は {orderAck}・{vendorQuote} を画面が t() で埋める */
export function checkOrder(x: OrderCheckInput): OrderCheck {
  const items: OrderCheckItem[] = [];
  const t = x.terms;
  // 未定（#251）：チェックだけで通す（理由は任意）。止めるのは「値が無く未定でもない」だけ
  const tbd = (k: OrderTbdKey) => !!t.tbd[k];
  if (!t.receiveTo && !tbd("receive")) items.push({ level: "fix", code: "receive", text: ORDER_CHECK_MSG.receive, action: "receive" });
  if (!x.place.trim() && !tbd("place")) items.push({ level: "fix", code: "place", text: ORDER_CHECK_MSG.place, action: "place" });
  if (!t.inspectOn && !tbd("inspect")) items.push({ level: "fix", code: "inspect", text: ORDER_CHECK_MSG.inspect, action: "inspect" });
  if (!t.payOn && !tbd("pay")) items.push({ level: "fix", code: "pay", text: ORDER_CHECK_MSG.pay, action: "pay" });
  // 約款（#233 2c、§14.2）：基本契約書の記録のある{vendor}は約款の行を出さない。添付しない＝何も出さない（#254）。
  // 止めるのは「自社の約款を選んだのに PDF が未登録」だけ（発行の瞬間に約款なしで固まってしまう）
  if (x.construction && !x.basicContract && t.terms === "own" && !x.ownTermsReady) items.push({ level: "fix", code: "termsOwn", text: ORDER_CHECK_MSG.termsOwn, action: "terms" });
  // 変更協議（#255・#261）：付けるときだけ確かめる（約款の付かない{order}は付けない＝帯に行が無い）
  if (x.construction && orderNegotiationApplies(t, !!x.basicContract) && (!t.negotiation.trim() || isNoChangeNegotiation(t.negotiation))) {
    items.push({ level: "fix", code: "negotiation", text: t.negotiation.trim() ? ORDER_CHECK_MSG.negotiationNoChange : ORDER_CHECK_MSG.negotiationEmpty, action: "negotiation" });
  }
  const pd = orderPayDays(t);
  if (pd && pd.days > ORDER_LIMITS.payDays) {
    items.push({ level: "warn", code: "payDays", text: ORDER_CHECK_MSG.payDays.replace("{pay}", t.payOn!.replaceAll("-", "/")).replace("{base}", md(pd.base)).replace("{days}", String(pd.days)), action: "pay" });
  }
  if (x.sourceTotal != null && x.subtotal < x.sourceTotal) {
    items.push({ level: "warn", code: "belowQuote", text: ORDER_CHECK_MSG.belowQuote.replace("{quote}", yen(x.sourceTotal)).replace("{diff}", yen(x.sourceTotal - x.subtotal)), action: null });
  }
  if (x.construction && x.field) {
    const n = x.lines.filter((l) => l.role === "detail" && (l.laborBreakdown?.labor == null)).length;
    if (n > 0) items.push({ level: "info", code: "labor", text: ORDER_CHECK_MSG.labor.replace("{n}", String(n)), action: null });
  }
  if (orderStampNeeded({ ack: x.ack, construction: x.construction, workKind: x.workKind, subtotal: x.subtotal })) items.push({ level: "info", code: "stamp", text: ORDER_CHECK_MSG.stamp, action: null });
  return { items, canIssue: !items.some((i) => i.level === "fix") };
}

/* 行の内訳（LaborBreakdown・LABOR_KEYS・LABOR_LABEL・laborSum）は行の型と一緒に estimate.ts（循環を避ける） */

/* ============================================================================
 * API の形
 * ========================================================================== */

/**
 * 承諾の状態（見出しのチップ）：draft＝下書き／unsent＝未送付／waiting＝承諾待ち（n日）／expired＝承諾待ちの期限切れ／paper_pending＝返送待ち（紙で受け取る＝承諾のリンクが無い・「紙で受け取りたい」のあと。「紙で回収」で paper に）／accepted＝承諾済／paper＝紙で回収／
 * issued＝発行済（承諾なしで発行した回。#253）
 */
export const OrderAckState = z.enum(["draft", "unsent", "waiting", "expired", "paper_pending", "accepted", "paper", "issued"]);
export type OrderAckState = z.infer<typeof OrderAckState>;

export const OrderAcceptance = z.object({
  method: z.enum(["screen", "paper"]),
  at: z.string(),
  recordNo: z.string(),
  /** 紙で回収：受け取った日 */
  receivedOn: z.string().nullable(),
  /** 承諾した人のメール（送ったメールの宛先。リンクで送ったときは null） */
  email: z.string().nullable(),
  /** 紙で回収の請書の写しがあるか */
  hasCopy: z.boolean(),
});
export type OrderAcceptance = z.infer<typeof OrderAcceptance>;

export const OrderLinkInfo = z.object({
  /** 承諾ページの URL（生きている鍵のときだけ。リンクをコピー・LINE・SMS） */
  url: z.string().nullable(),
  createdAt: z.string(),
  expiresAt: z.string(),
  openedAt: z.string().nullable(),
  /** 生きているか（無効・期限切れは false） */
  active: z.boolean(),
});
export type OrderLinkInfo = z.infer<typeof OrderLinkInfo>;

export const OrderSourceInfo = z.object({
  vendorQuoteId: z.string().uuid(),
  quoteNo: z.string(),
  /** 税抜の合計（発行前の確認「見積より低い」） */
  totalExTax: z.number().int().nullable(),
});
export type OrderSourceInfo = z.infer<typeof OrderSourceInfo>;

/** 注文書：見積の形（行・内訳・計算の見え方は見積のエディタ）に、注文書だけの欄を足したもの */
export const Order = Estimate.extend({
  revision: Estimate.shape.revision.extend({ terms: OrderTerms }),
  /** {vendor}の種別（工事／仕入）と、それがどこで決まったか（取引先／製品の既定） */
  workKind: OrderWorkKind,
  workKindSource: z.enum(["partner", "default"]),
  /** 建設の紙（約款添付型＝field の会社 かつ 工事） */
  construction: z.boolean(),
  /**
   * 承諾の方法（#253）：会社の既定・{vendor}ごと（null＝会社に従う）・この1回の初めの値・出した回に固めた方法（下書きは null。
   * 前に出した回＝固めた値の無い回は 承諾ページの鍵があれば electronic・無ければ paper）
   */
  ack: z.object({ company: OrderAckMode, partner: OrderAckMode.nullable(), initial: OrderAckMode, issued: OrderAckMode.nullable() }),
  /** 承諾が済んだか＝承諾の記録がある か 承諾なしで発行した（DB の fn_order_ack_done と同じ）。仕入登録・{paymentNotice}・訂正版の入口 */
  ackDone: z.boolean(),
  /**
   * 会社の約款の設定（#261。条件の帯の「約款」「変更協議」の行の出し分けと、帯で約款をオンにしたときの種類・変更協議）。
   * kind＝none は会社が約款オフ（帯に約款の行を出さない＝その{order}の約款がオンのときだけ出す）
   */
  companyTerms: z.object({ kind: OrderTermsKind, ownName: z.string(), negotiationOn: z.boolean(), negotiation: z.string() }).default({ kind: "none", ownName: "", negotiationOn: false, negotiation: "" }),
  /** {vendor}の支払条件の文（「月末締め・翌月末払い」。無ければ null＝新しい{order}の支払期日は未定で始まる。#259） */
  payTerms: z.string().nullable().default(null),
  /** {vendor}が電子での承諾を済ませているか（初回の説明の2行を出すか） */
  consented: z.boolean(),
  ackState: OrderAckState,
  /** 承諾待ちの日数（承諾待ちのときだけ） */
  waitingDays: z.number().int().nullable(),
  acceptance: OrderAcceptance.nullable(),
  link: OrderLinkInfo.nullable(),
  /** 元の{vendorQuote}（写して作ったとき） */
  source: OrderSourceInfo.nullable(),
  /** 元の見積依頼・{vendorQuote}・見積（#233 2a。頭の帯の「元」＝2件以上なら「見積依頼 n件 ›」） */
  origins: OrderOrigins.default({ rfqs: [], vendorQuotes: [], estimates: [] }),
  /** 発行前の確認（下書きのときだけ。出したあとは空） */
  check: OrderCheck,
  /** 帳票の名前（下書き＝今の会社の言葉・出したあと＝固めた名前） */
  names: z.object({ order: z.string(), orderAck: z.string() }),
  /** 印字項目（下書き＝会社の既定・出したあと＝固めた値） */
  print: z.object({ breakdown: z.boolean(), seal: z.boolean() }),
  /** 取消しの断り（承諾済み・仕入か{paymentNotice}が付いている＝#253） */
  unissueBlockedBy: z.enum(["accepted", "purchase"]).nullable(),
  /** 基本契約書（下書き＝{vendor}の今の記録・出したあと＝固めた値。建設の{order}だけ。#233 2c） */
  basicContract: BasicContractTerm.nullable(),
  /**
   * 印刷・FAX の約款（#233 2c、§14.2）：switch＝約款を別紙にできる（送付状の枠にスイッチ「約款」）／missing＝建設なのに約款が無い（薄墨の1行）／
   * none＝出さない（仕入・基本契約書の記録あり）。default＝スイッチの初めの値（会社の「印刷で約款」）
   */
  termsPrint: z.object({ mode: z.enum(["none", "switch", "missing"]), default: z.boolean() }),
  /** 訂正版（#233 2b）：紙の画面のスイッチ「変更点」の初めの値（{vendor}ごとの前回の選択 partners.order_change_sheet。無ければオン） */
  changeSheetDefault: z.boolean().default(true),
  /** 訂正版（#233 2b）：前の回（2回目から。直している下書きなら出ている回）の番号と承諾の記録（記録番号・方法・日時）。1回目は null */
  prevRound: z
    .object({ no: z.number().int(), displayNo: z.string(), recordNo: z.string().nullable(), method: z.enum(["screen", "paper"]).nullable(), at: z.string().nullable() })
    .nullable()
    .default(null),
});
export type Order = z.infer<typeof Order>;
/** 印刷・FAX の約款の出し方（API の loadOrder と画面が同じ関数） */
export function orderTermsPrintMode(x: { construction: boolean; basicContract: unknown; terms: OrderTermsKind }): "none" | "switch" | "missing" {
  if (!x.construction || x.basicContract) return "none";
  return x.terms === "none" ? "missing" : "switch";
}

export const OrderSummary = z.object({
  id: z.string().uuid(),
  documentNo: z.string().nullable(),
  status: EstimateStatus,
  vendorId: z.string().uuid(),
  vendorName: z.string(),
  group: z.object({ id: z.string().uuid(), name: z.string() }).nullable(),
  subject: z.string(),
  subtotal: z.number().int(),
  total: z.number().int(),
  issueDate: z.string(),
  ackState: OrderAckState,
  waitingDays: z.number().int().nullable(),
  acceptedAt: z.string().nullable(),
  /** 出ている回（#233 2b。2回目からはチップに「（2回目）」・番号は枝番） */
  revisionNo: z.number().int().default(1),
  displayNo: z.string().nullable().default(null),
  updatedAt: z.string(),
});
export type OrderSummary = z.infer<typeof OrderSummary>;
/**
 * 一覧のタブ（docs/93 §4.1・§16）：下書き／承諾待ち／承諾済／発行済（承諾なしで発行）／すべて。
 * 出すタブは orderTabsShown（承諾を使う会社は 承諾待ち・承諾済、なしの会社は 発行済。どちらも件数があれば出す）
 */
export const OrderTab = z.enum(["draft", "waiting", "accepted", "issued", "all"]);
export type OrderTab = z.infer<typeof OrderTab>;
export const OrderList = z.object({
  items: z.array(OrderSummary),
  counts: z.record(OrderTab, z.number().int()),
  /** 会社の承諾の既定（タブの出し分け） */
  ackMode: OrderAckMode.default("none"),
});
export type OrderList = z.infer<typeof OrderList>;
/** 一覧に出すタブ（#253）：会社の既定がなしなら 下書き／発行済／すべて、承諾を使うなら 下書き／承諾待ち／承諾済／すべて。件数のあるタブは足す */
export function orderTabsShown(ackMode: OrderAckMode, counts: Partial<Record<OrderTab, number>>): OrderTab[] {
  const uses = ackMode !== "none";
  return (["draft", "waiting", "accepted", "issued", "all"] as const).filter((k) =>
    k === "draft" || k === "all" ? true : k === "issued" ? !uses || (counts.issued ?? 0) > 0 : uses || (counts[k] ?? 0) > 0,
  );
}
/** 状態 → タブ（API の一覧と画面が同じ決まり） */
export function orderTabOf(s: OrderAckState): Exclude<OrderTab, "all"> {
  return s === "draft" ? "draft" : s === "accepted" || s === "paper" ? "accepted" : s === "issued" ? "issued" : "waiting";
}

/** POST /orders：{vendor}と件名だけ（{project}は任意）・{vendorQuote}から（回答の行を写す） */
export const OrderCreate = z
  .object({
    partnerId: z.string().uuid().optional(),
    projectGroupId: z.string().uuid().nullable().optional(),
    subject: z.string().trim().max(200).optional(),
    fromVendorQuoteId: z.string().uuid().optional(),
  })
  .refine((x) => !!x.partnerId || !!x.fromVendorQuoteId, { message: "{vendor}を選んでください" });
export type OrderCreate = z.infer<typeof OrderCreate>;

/** PUT /orders/:id。省いた欄は変えない。版（updatedAt）が違えば 409 ORDER_STALE */
export const OrderSave = z.object({
  subject: z.string().max(200).optional(),
  issueDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  /** 納品場所（暗号化して回の site_address_enc） */
  place: z.string().max(200).optional(),
  notes: z.string().max(2000).optional(),
  terms: OrderTerms.optional(),
  /** {vendor}を選び直す（下書きだけ。外注先・両方だけ） */
  partnerId: z.string().uuid().optional(),
  /** {project}の付け替え（null で外す） */
  projectGroupId: z.string().uuid().nullable().optional(),
  sheets: z.array(SheetInput).max(50).optional(),
  lines: z.array(LineInput).max(1000).optional(),
  version: z.string().min(1).max(40).optional(),
});
export type OrderSave = z.infer<typeof OrderSave>;

/** POST /orders/:id/issue。ack＝この1回の承諾の方法（省けば Order.ack.initial）。eAck は前の形（true＝電子・false＝紙。ack があれば見ない） */
export const OrderIssueRequest = z.object({
  ack: OrderAckMode.optional(),
  eAck: z.boolean().optional(),
  /** 印字項目（送信設定の帯。省けば会社の既定） */
  print: z.object({ breakdown: z.boolean(), seal: z.boolean() }).optional(),
  company: CompanyUpdate.optional(),
  version: z.string().min(1).max(40).optional(),
});
export type OrderIssueRequest = z.infer<typeof OrderIssueRequest>;
export const OrderIssueResult = z.object({ documentNo: z.string(), issuedAt: z.string(), updatedAt: z.string(), linkUrl: z.string().nullable() });
export type OrderIssueResult = z.infer<typeof OrderIssueResult>;

/** POST /orders/:id/ack/paper：紙で回収（受け取った日は必須・請書の写しは任意＝POST /orders/:id/ack/copy で上げた file） */
export const OrderPaperAck = z.object({ receivedOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/), copyAssetId: z.string().uuid().nullable().default(null) });
export type OrderPaperAck = z.infer<typeof OrderPaperAck>;
/** POST /orders/:id/ack/mode：発行したあとで電子承諾を切り替える（電子 → 紙＝鍵を無効に・紙 → 電子＝承諾がまだなら鍵を作る）。承諾なしで発行した回は 409 ORDER_ACK_NONE */
export const OrderAckModeChange = z.object({ eAck: z.boolean() });
/** POST /orders/:id/ack/sent：リンクを送った（コピー・LINE・SMS）＝送付の記録 */
export const OrderLinkSent = z.object({ via: z.enum(["copy", "line", "sms"]) });

/** {vendor}の詳細の節「{order}」（GET /partners/:id/order） */
export const ConsentRecord = z.object({
  id: z.string().uuid(),
  event: z.enum(["consent", "revoke"]),
  method: z.enum(["screen", "line", "mail", "paper"]),
  on: z.string(),
  at: z.string(),
  /** 写しのファイル名（元請の記録） */
  copyName: z.string().nullable(),
  copyAssetId: z.string().uuid().nullable(),
  /** 画面での承諾：その時の{order}の番号 */
  docNo: z.string().nullable(),
});
export type ConsentRecord = z.infer<typeof ConsentRecord>;
export const PartnerOrderInfo = z.object({
  ackMode: OrderAckMode.nullable(),
  workKind: OrderWorkKind.nullable(),
  workKindDefault: OrderWorkKind,
  /** 会社の承諾の既定（#253。「会社に従う（会社：○）」） */
  companyAck: OrderAckMode,
  consented: z.boolean(),
  /** 元請の記録（LINE・メール・書面） */
  records: z.array(ConsentRecord),
  /** 初回の承諾（画面） */
  firstScreen: ConsentRecord.nullable(),
  /** 基本契約書の記録（#233 2c）。fileUrl＝PDF の署名つき URL（15〜30分） */
  basicContract: BasicContractTerm.extend({ fileName: z.string().nullable(), fileUrl: z.string().nullable() }).nullable(),
  /** この{vendor}に出す{order}の書式（#233 2d。null＝会社の既定） */
  paper: PaperRef.nullable().default(null),
  updatedAt: z.string(),
});
export type PartnerOrderInfo = z.infer<typeof PartnerOrderInfo>;
/** PUT /partners/:id/basic-contract（追加・修正。版が違えば 409 PARTNER_STALE）。assetId＝POST /partners/:id/basic-contract/file で上げた PDF（null＝外す・省く＝そのまま） */
export const BasicContractSave = BasicContractTerm.extend({ assetId: z.string().uuid().nullable().optional(), version: z.string().min(1).max(40) });
export type BasicContractSave = z.infer<typeof BasicContractSave>;
export const PartnerOrderUpdate = z.object({
  ackMode: OrderAckMode.nullable().optional(),
  workKind: OrderWorkKind.nullable().optional(),
  /** {order}の書式（#233 2d。null＝会社の既定に従う。管理者だけ） */
  paper: PaperRef.nullable().optional(),
  version: z.string().min(1).max(40).optional(),
});
export type PartnerOrderUpdate = z.infer<typeof PartnerOrderUpdate>;
/** 出す瞬間に固める{order}の書式（#233 2d。template_snapshot.paper）。無い回（2d より前）は標準（A4縦）＝今の紙 */
export const OrderPaperSnap = z.object({
  ref: PaperRef,
  source: PaperSource,
  preset: PaperPresetKey,
  name: z.string().optional(),
  /** 自分の書式：中身をまるごと（あとで直しても消しても出した紙は変わらない） */
  layout: PaperLayout.optional(),
});
export type OrderPaperSnap = z.infer<typeof OrderPaperSnap>;
/** {order}の書式の文（#233 2d） */
export const ORDER_PAPER_MSG = {
  constructionLocked: "建設の{order}の書式には、工事名と「記載のない事項は…」の1行が要ります",
  kindMismatch: "その書式は{order}には使えません",
} as const;

/** 元請の「電子でよいと確認済み」（口頭は不可＝方法に無い） */
export const ConsentCreate = z.object({ on: z.string().regex(/^\d{4}-\d{2}-\d{2}$/), method: z.enum(["line", "mail", "paper"]), copyAssetId: z.string().uuid().nullable().default(null) });
export type ConsentCreate = z.infer<typeof ConsentCreate>;

/** 設定 → {order}（GET/PUT /company/order-settings） */
export const OrderSettingsPage = z.object({
  settings: OrderSettings,
  names: z.object({ order: z.string(), orderAck: z.string(), paymentNotice: z.string() }),
  termsFileName: z.string().nullable(),
  /** {order}兼請書を既定に（#233 2d）＝会社の既定の書式（tenants.paper_order_ref）が preset:combo-p。持つ場所は帳票デザインの既定と同じ1つ */
  combined: z.boolean(),
});
export type OrderSettingsPage = z.infer<typeof OrderSettingsPage>;
export const OrderSettingsUpdate = z.object({
  /** 承諾の既定（なし／電子／紙。#253） */
  ackMode: OrderAckMode.optional(),
  termsKind: OrderTermsKind.optional(),
  termsAssetId: z.string().uuid().nullable().optional(),
  termsName: z.string().trim().max(60).optional(),
  negotiation: z.string().trim().max(1000).optional(),
  /** 変更協議を付ける既定（#255） */
  negotiationOn: z.boolean().optional(),
  printBreakdown: z.boolean().optional(),
  printSeal: z.boolean().optional(),
  printTerms: z.boolean().optional(),
  /** {order}兼請書を既定に（#233 2d）。true＝会社の既定の書式を preset:combo-p に・false＝兼請書が既定なら標準に戻す（自分の書式が既定なら触らない） */
  combined: z.boolean().optional(),
  order: z.string().trim().min(1).max(12).optional(),
  orderAck: z.string().trim().min(1).max(12).optional(),
  /** {paymentNotice}の名前（支払通知書⇄支払明細書。#233 3b） */
  paymentNotice: z.string().trim().min(1).max(12).optional(),
  /** {paymentNotice}の確認の期限（送付後 n日。3〜30。#233 3b） */
  confirmDays: z.number().int().min(3, "3〜30日で入れてください").max(30, "3〜30日で入れてください").optional(),
});
export type OrderSettingsUpdate = z.infer<typeof OrderSettingsUpdate>;

/** 条件の欄の組（#233 2b。DB の fn_order_revision_changes が返す名前と同じ） */
export const OrderTermKey = z.enum(["receive", "place", "inspect", "pay", "method", "terms", "negotiation", "breakdown"]);
export type OrderTermKey = z.infer<typeof OrderTermKey>;
/** 条件の変更の1行（前回 → 今回。old が "" なら今回だけを出す＝行の内訳など） */
export const OrderTermChange = z.object({ key: OrderTermKey, label: z.string(), old: z.string(), new: z.string() });
export type OrderTermChange = z.infer<typeof OrderTermChange>;

/** 承諾ページ（公開の口 GET /api/v1/o/:token）。その{order}と承諾の結果だけ（社内メモ・原価・ほかの{vendor}は返さない） */
export const OrderPublicPage = z.object({
  company: z.string(),
  product: z.enum(["genba", "eigyo"]),
  title: z.string(),
  ackTitle: z.string(),
  docNo: z.string(),
  subject: z.string(),
  total: z.number().int(),
  issueDate: z.string(),
  expiresAt: z.string(),
  pages: z.number().int(),
  hasTerms: z.boolean(),
  /** 初回（その{vendor}に電子での承諾の記録が無い）＝ボタンの上に説明の2行 */
  firstTime: z.boolean(),
  /** 「紙で受け取りたい」の窓に収入印紙の参考を出すか（請負・税抜 1万円以上。#252） */
  stamp: z.boolean().default(false),
  /** 送った会社でログイン中の人（確かめ用に見るだけ＝押せない） */
  sender: z.boolean(),
  accepted: z.object({ at: z.string(), recordNo: z.string() }).nullable(),
  /** 何回目か（#233 2b。2回目から＝訂正版） */
  round: z.number().int().default(1),
  /** 訂正版の「変更点」（#233 2b。紙の上の表）：前回の番号・前回の承諾の記録番号・合計の増減・条件の変更・変えた行。1回目は null */
  changes: z
    .object({
      prevNo: z.string(),
      prevRecordNo: z.string().nullable(),
      prevTotal: z.number().int(),
      terms: z.array(OrderTermChange),
      rows: z.array(RevisionDiffRow),
    })
    .nullable()
    .default(null),
});
export type OrderPublicPage = z.infer<typeof OrderPublicPage>;
export const OrderAcceptResult = z.object({ at: z.string(), recordNo: z.string() });
export type OrderAcceptResult = z.infer<typeof OrderAcceptResult>;

/** 初回の説明の文の版（partner_consents.text_version＝表示した文面の版。文を変えたら版を上げる） */
export const ORDER_CONSENT_TEXT_VERSION = "o1-2026-10-24";

/** 承諾ページの鍵の形（閲覧ページと同じ＝32バイトの base64url 43字） */
export const ORDER_TOKEN_RE = /^[A-Za-z0-9_-]{43}$/;

/* ============================================================================
 * 文（画面・メール・紙）。{order} {orderAck} {vendor} {vendorQuote} {project} はその会社の言葉（画面は t()、API は fillLabels）
 * ========================================================================== */

export const ORDER_MSG = {
  menu: "{order}",
  list: "{order}",
  create: "{order}作成",
  newTitle: "{order}作成",
  notFound: "その{order}は見つかりません",
  vendorRequired: "{vendor}を選んでください",
  vendorInvalid: "{order}の宛先は{vendor}だけです",
  notDraft: "発行した{order}は書き換えられません。修正するときは取消しで下書きに戻します",
  issue: "発行",
  issueTitle: "発行前の確認",
  issueConfirm: "発行",
  issueBlocked: "要修正 {n}件を修正すると発行できます",
  issued: "{order}を発行しました。番号 {no} が付きました。",
  companyRequired: "会社名を入れてください。{order}の発行者として印字されます",
  noLines: "明細が1行もありません。行を追加してから発行してください",
  stale: "他の利用者が変更しました。再読込しますか？",
  unissue: "取り消して下書きに戻す",
  unissued: "{no} を取り消して下書きに戻しました。",
  unissueAccepted: "承諾済みの{order}は取り消せません",
  unissuePurchase: "仕入・{paymentNotice}の付いた{order}は取り消せません",
  /** 承諾なしで発行した回（#253）：承諾の記録・リンク・請書は無い */
  ackNoneIssued: "承諾なしで発行した{order}です",
  unissueDisabled: "発行した帳票の取消しは、設定で止めています",
  deleteDraft: "下書き削除",
  pdfDraft: "PDF で確認（透かし入り）",
  stateMenu: "発行した{order}のほかの動き",
  /** 頭の帯 */
  source: "元",
  detail: "詳細",
  /** 条件の帯 */
  terms: "条件",
  expand: "広げる",
  collapse: "閉じる",
  receive: "受領期日",
  receiveConstruction: "受領期日（工期）",
  place: "納品場所",
  placeFromGroup: "{project}の現場住所",
  inspect: "検査完了期日",
  pay: "支払期日",
  payMethod: "支払方法",
  termsDoc: "約款",
  termsNone: "なし",
  negotiation: "変更協議",
  negotiationShort: "定型",
  negotiationEdited: "修正済",
  /** 条件の帯の変更協議の「修正 ›」（#258。押すと文の窓） */
  negotiationEdit: "修正 ›",
  notEntered: "未入力",
  tbd: "未定",
  tbdReason: "未定の理由",
  tbdOn: "予定日",
  /** 「未定」の右の小さなボタン（#258。押すと理由と予定日の窓＝任意） */
  tbdNote: "理由…",
  tbdNoteTitle: "未定の理由",
  payDaysNote: "受領から {days}日",
  constructionChip: "建設工事・約款添付型",
  constructionChipBasic: "建設工事・基本契約書",
  termsShow: "約款 ›",
  termsAttach: "標準約款添付",
  /** 基本契約書の記録のある{vendor}（#233 2c）：条件の帯の約款の欄 */
  basicContract: "基本契約書",
  basicContractNote: "{vendor}の基本契約書の記録があるため、約款は付けません。",
  /** 印刷・FAX の約款（送付状の枠のスイッチ。#233 2c） */
  termsPrint: "約款",
  termsPrintHelp:
    "建設工事の{order}は、基本契約書か約款の添付で契約します（建設業法 19条）。{vendor}と基本契約書を交わしているときは、{vendor}の詳細の「基本契約書」に記録すると約款が要らなくなります。",
  /** 送信設定の帯 */
  sendSettings: "送信設定",
  to: "送信先",
  via: "送信方法",
  viaPrint: "印刷・FAX",
  viaMail: "メール",
  viaLink: "リンク",
  attach: "添付",
  print: "印字項目",
  printBreakdown: "内訳表",
  printSeal: "社印",
  ack: "承諾",
  eAck: "電子承諾",
  identity: "本人確認：リンクのみ",
  remember: "次回も同じ設定",
  paperStamp: "書面の{orderAck}には収入印紙（200円〜）が必要です",
  /** 見出しのチップ */
  chipDraft: "下書き",
  /** 発行を取消して下書きに戻した回（番号あり。#276＝見積書の #273 と同じ言葉） */
  chipUnissued: UNISSUED_MSG.chip,
  chipUnsent: "未送付",
  chipWaiting: "承諾待ち {n}日",
  chipExpired: "承諾待ちの期限切れ",
  chipPaperPending: "返送待ち",
  chipAccepted: "承諾済 {md}",
  chipPaper: "書面で回収 {md}",
  /** 承諾なしで発行した回（#253） */
  chipIssued: "発行済",
  /** 紙の画面の右の枠（リンク） */
  linkCopy: "リンクコピー",
  linkLine: "LINE で送る",
  linkSms: "SMS で送る",
  linkCopied: "承諾ページのリンクをコピーしました",
  linkCopyFailed: "コピーできませんでした。リンクの欄の文字を選んでコピーしてください",
  linkReissue: "承諾リンク再発行",
  linkReissued: "承諾のリンクを出し直しました。前のリンクは使えません",
  linkNone: "電子承諾がオフです",
  linkExpires: "期限 {date}",
  paperAck: "書面で回収",
  paperAckTitle: "書面で回収",
  paperAckOn: "受け取った日",
  paperAckCopy: "請書控え（PDF・任意）",
  paperAckSave: "書面で回収記録",
  paperAckDone: "書面の{orderAck}を記録しました。",
  ackRecord: "承諾の記録",
  ackAt: "日時",
  ackBy: "承諾した人",
  ackMethod: "方法",
  ackRecordNo: "記録番号",
  ackScreen: "画面",
  ackPaper: "書面",
  ackPdf: "{orderAck}（PDF）",
  /** 右下の通知 */
  noticeAccepted: "{vendor}「{name}」が{order} {no} を承諾しました",
  noticeDeclined: "{vendor}「{name}」が{order} {no} を書面で受け取りたいと連絡しました",
  /** 一覧 */
  tabDraft: "下書き",
  tabWaiting: "承諾待ち",
  tabAccepted: "承諾済",
  tabIssued: "発行済",
  tabAll: "すべて",
  colNo: "No.",
  colVendor: "{vendor}",
  colProject: "{project}",
  colSubject: "件名",
  colAmount: "金額（税抜）",
  colIssue: "発行日",
  colAck: "承諾",
  empty: "{order}はまだありません",
  /** {project}のタブ・{vendorQuote}の詳細 */
  groupTab: "{order}",
  fromVendorQuote: "{order}作成",
  createdFromVendorQuote: "{vendorQuote} {no} の行を写して{order}を作成しました。",
} as const;

/** 発行前の確認の文（§5.2）。要修正＝止める・注意＝出せる・参考＝出せる */
export const ORDER_CHECK_MSG = {
  fixChip: "要修正 {n}",
  warnChip: "注意 {n}",
  infoChip: "参考 {n}",
  fixLead: "発行できません",
  warnLead: "発行できます",
  receive: "受領期日が未入力です（「未定」にすると発行できます）",
  place: "納品場所が未入力です（「未定」にすると発行できます）",
  inspect: "検査完了期日が未入力です（「未定」にすると発行できます）",
  pay: "支払期日が未入力です（「未定」にすると発行できます）",
  termsOwn: "自社の約款の PDF が未登録です（設定 → {order}で登録するか、標準約款を添付）。",
  negotiationEmpty: "変更協議の文が未入力です（建設工事は価格変動時の変更の方法が必要）。",
  negotiationNoChange: "変更協議の文が「変更しない」型です（建設工事は価格変動時に協議できる文が必要）。",
  payDays: "支払期日 {pay} は受領（{base}）から {days}日です。上限は 60日です（取適法）。",
  belowQuote: "{vendorQuote}の金額 {quote}円 より {diff}円 低い金額です。",
  labor: "労務費の内訳が未入力の行が {n}行あります。",
  stamp: "書面の{orderAck}には収入印紙（200円〜）が必要です。",
  input: "入力",
} as const;

/** 承諾ページ（ログインなし・ライト・スマホ前提）。{company} は発行した会社名 */
export const ORDER_PUBLIC_MSG = {
  from: "発行：{company}",
  total: "合計（税込）",
  issueDate: "発行日",
  expires: "承諾期限",
  terms: "約款 ›",
  pdf: "PDF 保存",
  accept: "承諾",
  accepting: "承諾しています…",
  firstTime: "内容を確かめて「承諾」を押すと{orderAck}になります。印紙と返送は要りません。次回からもこの画面で届きます。",
  acceptedChip: "承諾済 {md}",
  acceptedAt: "承諾しました（{at}）",
  recordNo: "記録番号 {no}",
  ackPdf: "{orderAck}（PDF）",
  paper: "書面で受け取りたい ›",
  paperTitle: "書面で受け取りたい",
  paperBody: "電子での承諾をやめ、書面の{order}で受け取ります。このリンクは無効になり、{company}に連絡が届きます。",
  paperStamp: "書面の{orderAck}には収入印紙（200円〜）が必要です。",
  paperConfirm: "書面で受け取る",
  paperClose: "閉じる",
  paperDone: "書面で受け取ると{company}に連絡しました。",
  sender: "送った会社でログイン中のため、確かめ用に表示しています（承諾できません）",
  page: "{n}／{total}",
  notFound: "このページは見つかりません",
  gone: "この{order}は承諾の受付を終了しました",
  goneExpired: "この{order}の承諾期限が過ぎました",
  goneBody: "{company}に連絡してください。",
  already: "この{order}はもう承諾されています",
  tooMany: "しばらくしてからもう一度お試しください",
  /** 訂正版（#233 2b）：紙の上の表の見出し */
  changes: "変更点",
} as const;

/** {vendor}の詳細の節・設定の画面 */
export const ORDER_SETTINGS_MSG = {
  title: "{order}",
  names: "名称",
  namesHelp: "帳票を選ぶと請書に対の名前が入ります。請書は手で修正できます。発行済みの紙は変わりません。",
  nameDoc: "帳票",
  nameAck: "請書",
  free: "自由入力",
  /** 承諾の既定（#253）→ 節「{orderAck}」のスイッチ（既定オフ）と受け取り方（#256） */
  ack: "{orderAck}",
  ackHelp:
    "{vendor}から{orderAck}を受け取るかの既定です。オフにすると発行で完了します。電子承諾＝{vendor}がリンクから確かめて「承諾」を押すと{orderAck}の PDF ができ、双方に届きます（印紙と返送は要りません）。書面＝署名欄の付いた{orderAck}を返送してもらい「書面で回収」で記録します。請負（工事）で税抜 1万円以上の書面の{orderAck}には収入印紙（200円〜）が必要です。{vendor}ごと・{order}ごとに変えられます。",
  /** 受け取り方（スイッチがオンのとき） */
  ackReceive: "受け取り方",
  identity: "本人確認",
  /** 本人確認の値（SMS の送り口がまだ無い＝リンクのみ。§17.3 ハ） */
  identityLink: "リンクのみ",
  terms: "約款",
  termsHelp: "建設工事の{order}は、約款を添付して契約します（建設業法 19条）。標準は国土交通省の建設工事標準下請契約約款です。{vendor}と基本契約書を交わしているときは、{vendor}の詳細の「基本契約書」に記録すると約款が要らなくなります。",
  termsStandard: "標準（国交省）",
  termsOwn: "自社の PDF",
  termsNone: "添付しない",
  termsOwnPick: "PDF 選択",
  printTerms: "印刷で約款",
  /** 約款の種類（#261） */
  termsKind: "種類",
  /** 約款の PDF の行（表示と自社の PDF の登録） */
  termsPdf: "約款 PDF",
  combined: "{order}兼請書",
  combinedHelp: "{order}の下に切り取り線と請書の欄を印字する書式を既定にします。{vendor}は下の請書に署名・押印して返送します。出すときに紙の画面の「書式」で変えられます。発行済みの紙は変わりません。",
  printTermsHelp: "印刷・FAX で{order}を送るとき、約款を別紙で同封する既定です。送付状の枠で、その1回だけ変えられます。基本契約書の記録がある{vendor}には付きません。",
  negotiation: "変更協議",
  /** 変更協議のスイッチ（#255。既定オフ） */
  negotiationOn: "変更協議",
  negotiationHelp: "価格変動時の請負代金の変更の方法（建設業法 19条1項）。「変更しない」型の文は発行できません。",
  negotiationReset: "標準に戻す",
  /** 定型文の行（#261。押すと文の窓） */
  negotiationText: "定型文",
  print: "印字項目",
  printHelp: "{order}の紙に印字するものの既定です。発行のたびに送信設定で変えられます。",
  /** {vendor}の詳細（#256：{orderAck}＝会社に従う／オン／オフ＋オンのとき受け取り方） */
  ackMode: "{orderAck}",
  ackOn: "オン",
  ackOff: "オフ",
  ackCompany: "会社に従う",
  ackCompanyNow: "会社に従う（会社：{v}）",
  /** 「会社：○」の○（オフ／電子承諾／書面） */
  ackCompanyOff: "オフ",
  workKind: "種別",
  consents: "電子でよいと確認済み",
  consentsHelp: "建設工事を電子で契約するには、{vendor}の承諾が要ります（書面か電子の方法。口頭だけの確認は記録できません）。LINE・メール・書面で確かめたら、日付と写しを記録します。初回の承諾の画面でも承諾を記録します。",
  consentAdd: "記録追加",
  consentAddTitle: "記録追加",
  consentOn: "日付",
  consentMethod: "方法",
  consentMethodLine: "LINE",
  consentMethodMail: "メール",
  consentMethodPaper: "書面",
  consentCopy: "写し",
  consentCopyPick: "ファイル選択",
  consentSave: "記録保存",
  consentShow: "表示 ›",
  firstScreen: "初回の承諾",
  firstScreenText: "{at}　画面（{no}）",
  /** 基本契約書の記録（#233 2c。工事の{vendor}だけ） */
  basicContract: "基本契約書",
  basicContractNone: "未登録",
  basicContractHelp: "記録があれば{order}に約款を付けず「基本契約書による」の1行になります。契約書そのものはこのシステムでは交わしません。",
  basicContractOn: "締結日",
  basicContractName: "名称",
  basicContractFile: "PDF（任意）",
  basicContractFileShow: "PDF 表示",
  basicContractFileRemove: "PDF を外す",
  basicContractNameRequired: "名称を入力してください",
  basicContractOnRequired: "締結日を入力してください",
  basicContractPdfOnly: "PDF を選んでください",
} as const;

/* ============================================================================
 * 訂正版と再承諾（#233 2b、docs/93 §14.5）
 *   ・承諾済（画面・紙で回収）の{order}だけ「訂正版作成」＝枝番 PO-0001-2 の下書き（承諾前は取消して同じ番号で出し直す）
 *   ・発行前の確認に「前回とのちがい」（条件の変更を1行ずつ＋行の 追加／変更／削除／計算）。差分ゼロは出せない
 *   ・発行すると前の回の承諾ページの鍵は無効（開くと「新しい{order}が届いています」）・新しい承諾ページに「変更点」の表
 *   ・承諾は回ごと（order_acceptances の revision_id）。前の回の承諾の記録・紙・送付の記録はそのまま残る
 * 変わったかどうかは DB（fn_revision_diff・fn_order_revision_changes）が決める。ここは文にするだけ
 * ========================================================================== */

/** 発行前の確認の「前回とのちがい」（GET /orders/:id/diff。直している下書きのときだけ） */
export const OrderRevisionDiff = z.object({
  /** 出ている回（前回）と、その承諾の記録 */
  base: z.object({
    revisionNo: z.number().int(),
    displayNo: z.string(),
    total: z.number().int(),
    subtotal: z.number().int(),
    acceptance: z.object({ method: z.enum(["screen", "paper"]), recordNo: z.string(), at: z.string() }).nullable(),
  }),
  /** 直している下書き（今回） */
  next: z.object({ revisionNo: z.number().int(), displayNo: z.string(), total: z.number().int(), subtotal: z.number().int() }),
  /** 条件の変更（表の上に1行ずつ） */
  terms: z.array(OrderTermChange),
  /** 行・内訳の紙・頭の欄（件名・備考）の変更（v_revision_diff。納品場所は terms の place） */
  rows: z.array(RevisionDiffRow),
  /** 変わっていない行の数 */
  unchangedCount: z.number().int(),
  /** 前回と同じ内容（行も条件も）＝発行できない */
  empty: z.boolean(),
});
export type OrderRevisionDiff = z.infer<typeof OrderRevisionDiff>;

/** POST /orders/:id/change-sheet：紙の画面のスイッチ「変更点」（{vendor}ごとに前回の選択を覚える） */
export const OrderChangeSheet = z.object({ on: z.boolean() });
export type OrderChangeSheet = z.infer<typeof OrderChangeSheet>;

/** 訂正版の文。{order} {orderAck} {vendor} はその会社の言葉（画面は t()、API は fillLabels） */
export const ORDER_REVISE_MSG = {
  /** 「⋯」の項目（承諾済の{order}だけ） */
  revise: "訂正版作成",
  reviseDone: "{no} の下書きを作成しました。",
  /** 承諾前は訂正版を作らない（取消して出し直す） */
  notAccepted: "承諾前の{order}は、取消して同じ番号で出し直します",
  draftExists: "訂正版の下書きがあります",
  tooMany: "1つの{order}を訂正できるのは 99回目までです",
  notRevising: "訂正版の下書きがありません",
  vendorLocked: "訂正版では{vendor}を変えられません。別の{vendor}に出すときは新しい{order}を作成します",
  /** 見出しの主ボタン（訂正版の下書き） */
  issue: "{no} として発行",
  issued: "{no} を発行しました。",
  /** 発行前の確認の中 */
  diffTitle: "前回とのちがい",
  diffLead: "{prev} → {no}",
  noDiff: "前回と同じ内容です",
  terms: "条件",
  lines: "明細",
  unchanged: "変わっていない {n}行 は載せていません。",
  prevAck: "前回の承諾の記録番号 {rec}",
  /** 取消し：訂正版の下書きがあるあいだ前の回は取り消せない */
  unissueHasRevision: "訂正版の下書きがあります。先に下書きを削除します",
  /** 訂正版の下書きの削除（その回だけ） */
  discardAsk: "{n}回目の下書きを削除しますか。{vendor}に出ている {prev}回目（{no}）はそのまま残ります",
  discardConfirm: "{n}回目削除",
  discarded: "{n}回目の下書きを削除しました。",
  /** 見出しのチップ */
  chipRevising: "訂正中 {n}回目",
  chipWaitingRevised: "承諾待ち（訂正）",
  chipAcceptedRound: "承諾済 {md}（{n}回目）",
  chipPaperRound: "書面で回収 {md}（{n}回目）",
  /** 比較 */
  compare: "比較",
  compareTitle: "{order}の比較",
  /** 紙の画面のスイッチ「変更点」（2回目から） */
  changeSheet: "変更点",
  changeSheetHelp: "最後のページに前回からの変更点（条件・変えた行・合計の増減）の表を付けます。{vendor}ごとに前回の選択を覚えます。",
  /** 回の帯の前の回（承諾の記録） */
  roundAck: "{orderAck}",
  roundPaperCopy: "請書控え",
  /** 条件の変更の行の名前（受領期日は建設では工期） */
  receive: "受領期日",
  receiveConstruction: "工期",
  place: "納品場所",
  inspect: "検査完了期日",
  pay: "支払期日",
  method: "支払方法",
  termsDoc: "約款",
  negotiation: "変更協議",
  breakdown: "行の内訳（材料費・労務費・法定福利費・安全衛生経費）",
  changed: "変更",
  none: "なし",
  tbd: "未定",
} as const;

const slashDay = (d: string | null | undefined): string => (d ? d.replace(/-/g, "/") : "");
/** 未定の欄（#251）：「未定」だけ（理由・予定日は社内の控え＝紙・承諾ページ・差分の文に出さない） */
const tbdText = (t: OrderTbd | undefined): string => (t ? ORDER_REVISE_MSG.tbd : "");
const cut = (s: string, n = 40): string => (s.length > n ? `${s.slice(0, n)}…` : s);

/** 約款の扱いの文（基本契約書・標準約款・自社の約款・なし） */
export function orderTermsDocText(t: Pick<OrderTerms, "terms" | "termsName" | "basicContract">): string {
  if (t.basicContract) return `${ORDER_MSG.basicContract}（${slashDay(t.basicContract.on)} ${t.basicContract.name}）`;
  return t.terms === "standard" ? STANDARD_TERMS.name : t.terms === "own" ? t.termsName || "自社の約款" : ORDER_REVISE_MSG.none;
}

/**
 * 条件の変更の行（発行前の確認・比較の画面・承諾ページの「変更点」・変更点のページが同じ文）。
 * 変わった組は DB（fn_order_revision_changes）が決めた keys。ここは keys の順に「前回 → 今回」の文にするだけ
 */
export function orderTermChangeRows(
  keys: readonly OrderTermKey[],
  a: { terms: OrderTerms; place: string },
  b: { terms: OrderTerms; place: string },
  x: { construction: boolean; breakdownLines?: readonly string[] | undefined },
): OrderTermChange[] {
  const M = ORDER_REVISE_MSG;
  const receive = (s: { terms: OrderTerms }) =>
    s.terms.tbd.receive
      ? tbdText(s.terms.tbd.receive)
      : s.terms.receiveFrom || s.terms.receiveTo
        ? `${slashDay(s.terms.receiveFrom)}${s.terms.receiveFrom && s.terms.receiveTo ? "〜" : ""}${slashDay(s.terms.receiveTo)}`
        : "";
  const day = (v: string | null, t: OrderTbd | undefined) => (t ? tbdText(t) : slashDay(v));
  const out: OrderTermChange[] = [];
  for (const k of keys) {
    if (k === "receive") out.push({ key: k, label: x.construction ? M.receiveConstruction : M.receive, old: receive(a), new: receive(b) });
    else if (k === "place") out.push({ key: k, label: M.place, old: a.terms.tbd.place ? tbdText(a.terms.tbd.place) : a.place, new: b.terms.tbd.place ? tbdText(b.terms.tbd.place) : b.place });
    else if (k === "inspect") out.push({ key: k, label: M.inspect, old: day(a.terms.inspectOn, a.terms.tbd.inspect), new: day(b.terms.inspectOn, b.terms.tbd.inspect) });
    else if (k === "pay") out.push({ key: k, label: M.pay, old: day(a.terms.payOn, a.terms.tbd.pay), new: day(b.terms.payOn, b.terms.tbd.pay) });
    else if (k === "method") out.push({ key: k, label: M.method, old: ORDER_PAY_METHOD_LABEL[a.terms.method], new: ORDER_PAY_METHOD_LABEL[b.terms.method] });
    else if (k === "terms") {
      let o = orderTermsDocText(a.terms);
      let n = orderTermsDocText(b.terms);
      // 同じ約款で版だけ変わった（改正）：版を添える
      if (o === n) {
        o = `${o}（${a.terms.termsVersion || "—"}）`;
        n = `${n}（${b.terms.termsVersion || "—"}）`;
      }
      out.push({ key: k, label: M.termsDoc, old: o, new: n });
    } else if (k === "negotiation") {
      // 変更協議（#255）：付けない回は「なし」
      const nego = (s: { terms: OrderTerms }) => (orderNegotiationOn(s.terms) ? cut(s.terms.negotiation.trim()) || M.none : M.none);
      out.push({ key: k, label: M.negotiation, old: nego(a), new: nego(b) });
    }
    else if (k === "breakdown") {
      // 内訳の変わった行の名前を添える（名前の無い行・名前が多いときは数で）
      const names = (x.breakdownLines ?? []).map((v) => v.trim()).filter(Boolean);
      const which = names.length > 3 ? `${names.slice(0, 3).join("、")} ほか ${names.length - 3}行` : names.join("、");
      out.push({ key: k, label: M.breakdown, old: "", new: which ? `${M.changed}（${which}）` : M.changed });
    }
  }
  return out;
}
